import { ActionModal, FormLayout } from '@kcftech/design-system';
import {
    IOutputSensorDataConfigSdResponse,
    OutputSensorDataConfigUpdateRequest,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import {
    displayNameRules,
    NotesMaxLength,
    notesRules,
} from 'pages/Settings/ThirdPartySensorModels/shared';
import {
    StyledTextArea,
    StyledTextInput,
} from 'pages/Settings/ThirdPartySensorModels/styled';
import { useForm } from 'react-hook-form';

export type ConfigurationFormData = Readonly<{
    displayName: string;
    notes: string;
}>;

export type EditConfigurationModalProps = Readonly<{
    configuration: IOutputSensorDataConfigSdResponse;
    onClose: (updated?: boolean) => void;
}>;

export function EditConfigurationModal({
    configuration,
    onClose,
}: EditConfigurationModalProps) {
    const { showToast } = useNotificationContext();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
    } = useForm<ConfigurationFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            displayName: configuration.Name,
            notes: configuration.Notes,
        },
    });

    const { mutate: editConfiguration, isPending: isSaving } = useMutation({
        mutationFn: (data: ConfigurationFormData) =>
            sdClient.updateOutputSensorDataConfig({
                configId: configuration.Id,
                body: new OutputSensorDataConfigUpdateRequest({
                    Name: data.displayName,
                    Notes: data.notes,
                }),
            }),

        onSuccess: () => {
            showToast({
                type: 'success',
                content: 'Successfully updated sensor configuration',
            });
            onClose(true);
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to update sensor configuration',
            }),
    });

    const onSave = (data: ConfigurationFormData) => {
        editConfiguration(data);
    };

    return (
        <ActionModal
            height={500}
            title="Edit Sensor Configuration"
            actionButtonText="Save"
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting) && !isSaving
            }
            onAction={handleSubmit(onSave)}
            onClose={onClose}
        >
            <FormLayout>
                <ValidatableLabel
                    label="Display Name"
                    required
                    error={errors.displayName}
                >
                    <StyledTextInput
                        placeholder="Enter a Display Name"
                        $lightBackground
                        {...register('displayName', displayNameRules)}
                    />
                </ValidatableLabel>
                <ValidatableLabel label="Notes" error={errors.notes}>
                    <StyledTextArea
                        minRows={8}
                        className="mt-5"
                        placeholder="Please provide more detail for context"
                        maxLength={NotesMaxLength}
                        showLengthIndicator
                        $lightBackground
                        {...register('notes', notesRules)}
                    />
                </ValidatableLabel>
            </FormLayout>
        </ActionModal>
    );
}
